import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { QrCode, Download, Loader2, Copy, Check, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

export default function DocumentQRCode({ docType, docId, documentTitle }) {
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchQRCode = async () => {
    setLoading(true);
    try {
      // Always regenerate QR code with current URL
      const result = await api.getDocumentQRCode(docType, docId);
      setQrData(result);
    } catch (error) {
      toast.error('Erreur lors de la génération du QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      // Reset and regenerate QR code each time dialog opens
      setQrData(null);
      fetchQRCode();
    }
  };

  const handleCopyLink = () => {
    if (qrData?.verification_url) {
      navigator.clipboard.writeText(qrData.verification_url);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (qrData?.qr_code_base64) {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${qrData.qr_code_base64}`;
      link.download = `qrcode_${docType}_${docId}.png`;
      link.click();
      toast.success('QR Code téléchargé !');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <QrCode className="h-4 w-4" />
          QR Code
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            QR Code de Vérification
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center space-y-4 py-4">
          {loading ? (
            <div className="flex flex-col items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-2" />
              <p className="text-sm text-gray-500">Génération du QR code...</p>
            </div>
          ) : qrData ? (
            <>
              {/* QR Code Image */}
              <div className="bg-white p-4 rounded-lg border shadow-sm">
                <img 
                  src={`data:image/png;base64,${qrData.qr_code_base64}`}
                  alt="QR Code de vérification"
                  className="w-48 h-48"
                />
              </div>
              
              {/* Document Title */}
              {documentTitle && (
                <p className="text-sm text-gray-600 text-center font-medium">
                  {documentTitle}
                </p>
              )}
              
              {/* Verification Code */}
              <div className="bg-gray-50 rounded-lg p-3 w-full">
                <p className="text-xs text-gray-500 mb-1">Code de vérification :</p>
                <p className="font-mono text-sm font-bold text-center">
                  {qrData.verification_code}
                </p>
              </div>

              {/* Current URL info */}
              <div className="bg-blue-50 rounded-lg p-3 w-full">
                <p className="text-xs text-blue-600 text-center">
                  URL de vérification générée pour : {window.location.origin}
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 w-full">
                <Button 
                  variant="outline" 
                  className="flex-1 gap-2"
                  onClick={handleCopyLink}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copied ? 'Copié !' : 'Copier le lien'}
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 gap-2"
                  onClick={handleDownload}
                >
                  <Download className="h-4 w-4" />
                  Télécharger
                </Button>
              </div>

              {/* Regenerate button */}
              <Button 
                variant="ghost" 
                size="sm"
                className="gap-2 text-gray-500"
                onClick={fetchQRCode}
              >
                <RefreshCw className="h-3 w-3" />
                Régénérer
              </Button>
              
              {/* Instructions */}
              <p className="text-xs text-gray-500 text-center">
                Scannez ce QR code ou utilisez le code de vérification pour 
                confirmer l'authenticité du document.
              </p>
            </>
          ) : (
            <p className="text-gray-500">Impossible de générer le QR code</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
